/**
 * Overview
 * ========
 * The metric names defined here map directly to the "metricName" values
 * returned from the BE for each metric. The only exceptions are the metrics
 * that are in ALL_CAPS_CASE. Those ones are "virtual metrics" which are
 * created here the FE, either by:
 *   1. Aggregating existing metrics (ie. CATALOG_TOTAL_METRIC)
 *   2. Doing an extra query with different query dates (ie.
 *      CURRENT_TOTAL_USERS_ALL_TIME and CURRENT_TOTAL_CUSTOM_EVENTS)
 */

/**
 * Messaging Metrics
 * =================
 */

export const EMAILS_SENT = 'EmailsSent';
/** Metric that includes both SMS and MMS sent */
export const SMS_SENT = 'SmsSent';
export const PUSH_NOTIFICATIONS_SENT = 'PushNotificationsSent';
export const WEB_PUSH_NOTIFICATIONS_SENT = 'WebPushNotificationsSent';
export const IN_APP_NOTIFICATIONS_SENT = 'InAppNotificationsSent';
export const WHATSAPP_SENT = 'WhatsAppSent';
export const SMS_SEGMENTS_SENT = 'SmsSegmentsSent';
export const SMS_SEGMENTS_TOTAL = 'SmsSegmentsTotal';
export const SMS_SEGMENTS_RECEIVED = 'SmsSegmentsReceived';
export const MMS_ATTACHMENTS_TOTAL = 'MmsAttachmentsTotal';
export const MMS_ATTACHMENTS_RECEIVED = 'MmsAttachmentsReceived';
export const MMS_ATTACHMENTS_SENT = 'MmsAttachmentsSent';
export const SUCCESSFUL_SMS_VERIFICATIONS = 'SuccessfulSmsVerifications';
export const SMS_BILLING_METRICS = [
  SMS_SEGMENTS_SENT,
  SMS_SEGMENTS_TOTAL,
  SMS_SEGMENTS_RECEIVED,
  MMS_ATTACHMENTS_TOTAL,
  MMS_ATTACHMENTS_RECEIVED,
  MMS_ATTACHMENTS_SENT,
  SUCCESSFUL_SMS_VERIFICATIONS,
];

export const SMS_METRICS = {
  mmsAttachmentsReceived: 'MmsAttachmentsReceived',
  mmsAttachmentsSent: 'MmsAttachmentsSent',
  mmsAttachmentsTotal: 'MmsAttachmentsTotal',
  smsSegmentsReceived: 'SmsSegmentsReceived',
  smsSegmentsSent: 'SmsSegmentsSent',
  smsSegmentsTotal: 'SmsSegmentsTotal',
  successfulSmsVerifications: 'SuccessfulSmsVerifications',
} as const;

export const MESSAGING_METRICS = {
  emailsSent: 'EmailsSent',
  bccEmailsSent: 'BCCEmailsSent',
  ccEmailsSent: 'CCEmailsSent',
  inAppNotificationsSent: 'InAppNotificationsSent',
  pushNotificationsSent: 'PushNotificationsSent',
  smsSent: 'SmsSent',
  webPushNotificationsSent: 'WebPushNotificationsSent',
  whatsAppSent: 'WhatsAppSent',
} as const;

/**
 * Users Metrics
 * =============
 * The TotalUsersAllTime metric is only being used from the "Current" query,
 * renamed to CURRENT_TOTAL_USERS_ALL_TIME.
 */
export const USERS = 'TotalUsersAllTime';
export const USERS_ADDED = 'TotalUsersAddedInPeriod';
export const TOTAL_USERS_HIGH_WATERMARK = 'TotalUsersHighWatermark';
export const ACTIVE_USERS = 'ActiveUsersAllTime';
export const ACTIVE_USERS_ADDED = 'ActiveUsersAddedInPeriod';
/**
 * Unlike most metrics, CURRENT_USERS is not a metric returned from the backend.
 * It is the "TotalUsersAllTime" but with an endDate of yesterday.
 */
export const CURRENT_USERS = 'CURRENT_TOTAL_USERS_ALL_TIME';
export const USERS_METRICS = {
  activeUsersAddedInPeriod: 'ActiveUsersAddedInPeriod',
  activeUsersHighWatermark: 'ActiveUsersHighWatermark',
  activeUsersAllTime: 'ActiveUsersAllTime',
  totalUsersAddedInPeriod: 'TotalUsersAddedInPeriod',
  totalUsersHighWatermark: 'TotalUsersHighWatermark',
  totalUsersAllTime: 'TotalUsersAllTime',
} as const;

/**
 * Custom Events Metrics
 * =====================
 */

export const CUSTOM_EVENTS_STORED = 'TotalCustomEvents';
export const TOTAL_CUSTOM_EVENTS_HIGH_WATERMARK = 'TotalCustomEventsHighWatermark';

/**
 * Unlike most metrics, CURRENT_TOTAL_CUSTOM_EVENTS is not a metric returned from
 * the backend. It is the "TotalCustomEvents" but with an endDate of yesterday.
 */
export const CURRENT_CUSTOM_EVENTS_STORED = 'CURRENT_TOTAL_CUSTOM_EVENTS';
export const CUSTOM_EVENTS_METRICS = {
  totalCustomEvents: 'TotalCustomEvents',
  totalCustomEventsHighWatermark: 'TotalCustomEventsHighWatermark',
} as const;

/**
 * Catalog Metrics
 * ===============
 */

export const CATALOG_TOTAL_METRIC = 'CATALOG_TOTAL_METRIC'; // Derived from totaling below 3 metrics
export const LOOKUP_REFERENCES = 'CatalogLookup';
export const COLLECTION_REFERENCES = 'CatalogCollection';
export const PRODUCT_RECOMMENDATIONS = 'ProductRecommendation';

export const CATALOG_METRICS = {
  catalogCollection: 'CatalogCollection',
  catalogLookup: 'CatalogLookup',
  productRecommendation: 'ProductRecommendation',
} as const;

/**
 * Smart ingest Metrics
 */
export const SMART_INGEST_METRICS = {
  productionSyncsCount: 'productionSyncsCount',
  testSyncsCount: 'testSyncsCount',
} as const;

/**
 * Journey events Metrics
 */
export const JVT_COUNT = 'JvtCount';

export const JOURNEY_EVENTS_METRICS = {
  jvtCount: 'JvtCount',
} as const;
